// @flow
/* eslint-env browser */
import { combineReducers } from 'redux';
import { get } from 'lodash';
import { AsyncRequest } from 'angieslist-app-platform';
import { replace } from 'react-router-redux';
import urlCatalog from '@angi/url-catalog';
import { enableLegalRedirect } from '../../../app/common/agreement/agreementActions';
import serviceProviderActions from '../../../app/common/serviceProvider/serviceProviderActions';
import serviceProviderActionKeys from '../../../app/common/serviceProvider/serviceProviderActionKeys';
import { getServiceProvider } from '../../../app/common/serviceProvider/serviceProviderReducer';
import type {
  GetCurrentUsersServiceProvidersResponse,
  GetServiceProvidersResponse,
} from '../../../app/common/serviceProvider/serviceProviderService';
import type { GetCurrentUsersServiceProvidersState, GetServiceProvidersState, GetServiceProviderState } from '../../../app/common/serviceProvider/serviceProviderReducer';
import type { Action } from '../../../app/types';
import businessListActionKeys from '../../../app/common/businessList/businessListActionKeys';
import businessListActions from '../../../app/common/businessList/businessListActions';

type GetCurrentUsersServiceProviderSuccessAction = Action<{
  body: GetCurrentUsersServiceProvidersResponse,
}>;

type GetServiceProvidersSuccessAction = Action<{
  body: GetServiceProvidersResponse,
}>;

type NewActivitySuccessAction = Action<{
  body: Object,
}>;

export type BusinessListReducerState = {
  getCurrentUsersServiceProviders: GetCurrentUsersServiceProvidersState,
  getServiceProvider: GetServiceProviderState,
  getServiceProviders: GetServiceProvidersState,
  getNewActivity: any,
};

const isClient = typeof window !== 'undefined';

export default (combineReducers({
  getCurrentUsersServiceProviders:
  AsyncRequest.createReducer(serviceProviderActionKeys.GET_CURRENT_USERS_SERVICE_PROVIDERS, {
    [serviceProviderActionKeys.GET_CURRENT_USERS_SERVICE_PROVIDERS_SUCCESS]:
      (
        state,
        action: GetCurrentUsersServiceProviderSuccessAction,
      ) => {
        const offset = state.getIn(['request', 'offset']);
        action.addSideEffect((store) => {
          const { serviceProviders } = action.payload.body;

          if (serviceProviders.length === 0 && process.env.IS_BROWSER) {
            // redirect on timeout to avoid Redux persist issue
            // where AsyncRequest is persisted as isLoading=true
            setTimeout(() => {
              // eslint-disable-next-line no-undef
              window.location.replace('/app/home');
            }, 1);
          } else {
            // allow pactsafe to redirect
            store.dispatch(enableLegalRedirect());
          }

          if (serviceProviders.length === 1 && offset === 0) {
            // if just requested page one and only one SP then go to dashboard for SP
            const [{ id }] = serviceProviders;
            if (isClient) {
              // eslint-disable-next-line no-undef
              const { search = '' } = window.location;
              store.dispatch(serviceProviderActions.getServiceProvider(id));
              store.dispatch(replace(`/app/home/${id}${search}`));
            }
          } else if (serviceProviders.length > 0) {
            // dispatch call to get SP details by Ids
            store.dispatch(serviceProviderActions
              .getServiceProvider(serviceProviders.map(x => x.id)));
          }
        });
        return AsyncRequest.setSuccess(state, action.payload.body);
      },
  }),
  getServiceProviders:
  AsyncRequest.createReducer(businessListActionKeys.GET_SERVICE_PROVIDERS, {
    [businessListActionKeys.GET_SERVICE_PROVIDERS_SUCCESS]:
      (
        state,
        action: GetServiceProvidersSuccessAction,
      ) => {
        action.addSideEffect((store) => {
          const { serviceProviders, totalCount } = action.payload.body;
          const shouldGetNewActivity = get(store.getState(), 'experiment.experiments.unifiedBusinessListNewActivity', false);
          if (serviceProviders.length > 0 || !process.env.IS_BROWSER) {
            store.dispatch(enableLegalRedirect());
          }

          const getProType = proType => proType || '';
          const adsServiceProviderIds = serviceProviders.filter(sp => getProType(sp.proType).toLowerCase() === 'ads').map(sp => sp.id);
          const leadsServiceProviderIds = serviceProviders.filter(sp => getProType(sp.proType).toLowerCase() === 'leads').map(sp => sp.id);
          // dispatch call to get SP details for Ads pros
          if (adsServiceProviderIds.length >= 1) {
            store.dispatch(serviceProviderActions
              .getAdsServiceProvider(adsServiceProviderIds));
          }

          if (shouldGetNewActivity) {
            store.dispatch(businessListActions.getNewActivity({
              adsServiceProviderIds,
              leadsServiceProviderIds,
            }));
          }

          if (totalCount === 1 && isClient) {
            // if only one SP returned
            // then go to the lead board if pro is Angi Leads and dashboard if Angi Ads
            const [{ id, proType }] = serviceProviders;
            if (getProType(proType).toLowerCase() === 'leads') {
              window.location.replace(urlCatalog.office.leads.homePage(id));
            } else {
              // eslint-disable-next-line no-undef
              const { search = '' } = window.location;
              store.dispatch(replace(`/app/home/${id}${search}`));
            }
          }
        });
        return AsyncRequest.setSuccess(state, action.payload.body);
      },
  }),
  getServiceProvider,
  getNewActivity:
    AsyncRequest.createReducer(businessListActionKeys.GET_NEW_ACTIVITY, {
      [businessListActionKeys.GET_NEW_ACTIVITY_SUCCESS]:
        (
          state,
          action: NewActivitySuccessAction,
        ) => {
          const adsActivityMap = {};
          const leadsActivityMap = {};
          if (
            action.payload.body &&
            action.payload.body.adsActivity &&
            Array.isArray(action.payload.body.adsActivity) &&
            action.payload.body.adsActivity.length > 0
          ) {
            action.payload.body.adsActivity.forEach((activity) => {
              adsActivityMap[activity.serviceProviderId] = {
                id: activity.serviceProviderId,
                unreadDealCount: activity.unreadDealClaimedCount,
                unreadQuoteCount: activity.unreadQuoteRequestCount,
                unreadLeadCount: activity.unreadLeadCount,
                unreadMessageCount: activity.unreadConversations,
                unreadReviewCount: activity.unreadReviewCount,
              };
            });
          }

          if (
            action.payload.body &&
            action.payload.body.leadsActivity &&
            Array.isArray(action.payload.body.leadsActivity) &&
            action.payload.body.leadsActivity.length > 0
          ) {
            action.payload.body.leadsActivity.forEach((activity) => {
              leadsActivityMap[`HA-${activity.serviceProviderId}`] = {
                id: `HA-${activity.serviceProviderId}`,
                unreadLeadCount: activity.unreadLeadCount,
                opportunityCount: activity.opportunityCount,
              };
            });
          }

          return { ...adsActivityMap, ...leadsActivityMap };
        },
    }),
}): any);



// WEBPACK FOOTER //
// ./src/modules/home/common/businessListReducer.js